import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Snackbar,
  Alert,
  Box,
  Typography,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  NotificationsActive as NotificationsActiveIcon
} from '@mui/icons-material';

const DashboardNotificationSnackbar = ({
  exchanges = ['MCX', 'NFO', 'CDS', 'NSE', 'ALL']
}) => {
  const theme = useTheme();

  const [open, setOpen] = useState(false);

  // Get socket messages from Redux store
  const socketMessages = useSelector(state => state.socketMessage || {});
  const brokerageMessage = useSelector(state => state.brokerage?.brokerageData || null);

  // Process current messages using the same logic as ExchangeMessageSlider
  const processedMessages = useMemo(() => {
    const currentMessages = [];

    // Add brokerage message if it exists (highest priority)
    if (brokerageMessage?.message) {
      currentMessages.push({
        id: 'brokerage-alert',
        messageText: brokerageMessage.message,
        messageType: 'brokerage',
        exchange: 'SYSTEM',
        timestamp: brokerageMessage.lastUpdatedAt || Date.now(),
        marketStatus: 'Open',
        isCurrent: true,
        severity: 'warning'
      });
    }

    // Get current messages from exchange-specific stores only
    if (socketMessages.exchangeMessages) {
      exchanges.forEach(exchange => {
        const exchangeData = socketMessages.exchangeMessages[exchange];
        if (exchangeData && exchangeData.currentMessage) {
          const message = exchangeData.currentMessage;
          const hasContent = message.genericMessage || message.bodMessage || message.holidayMessage;

          if (message.marketStatus === "Open" && hasContent) {
            currentMessages.push({
              ...message,
              id: message.id || `current-${exchange}-${message.timestamp}`,
              isCurrent: true
            });
          }
        }
      });
    }

    // Fallback: Get current message from general store if exchange-specific is not available
    if (currentMessages.length === 0 && socketMessages.currentMessage) {
      const msg = socketMessages.currentMessage;
      const hasContent = msg.genericMessage || msg.bodMessage || msg.holidayMessage;

      if (exchanges.includes(msg.exchange) && msg.marketStatus === "Open" && hasContent) {
        currentMessages.push({
          ...msg,
          id: msg.id || `current-${msg.exchange}-${msg.timestamp}`,
          isCurrent: true
        });
      }
    }
    return currentMessages.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }, [socketMessages, exchanges, brokerageMessage]);

  const [currentIndex, setCurrentIndex] = useState(() => {
    const savedIndex = localStorage.getItem('dashboardNotificationIndex');
    return savedIndex ? parseInt(savedIndex, 10) : 0;
  });

  useEffect(() => {
    if (processedMessages.length > 0 && currentIndex < processedMessages.length) {
      const isClosed = localStorage.getItem('dashboardNotificationClosed');
      if (!isClosed) {
        setOpen(true);
      }
    }
  }, [processedMessages.length, currentIndex]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);

    if (currentIndex < processedMessages.length - 1) {
      setTimeout(() => {
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
        localStorage.setItem('dashboardNotificationIndex', nextIndex.toString());
        setOpen(true);
      }, 300);
    } else {
      localStorage.setItem('dashboardNotificationClosed', 'true');
      localStorage.removeItem('dashboardNotificationIndex');
    }
  };

  const getMessageSeverity = (message) => {
    if (message.messageType === 'brokerage') return message.severity || 'warning';
    if (message.genericMessage) return 'warning';

    const text = (message.genericMessage || message.bodMessage || '').toLowerCase();
    if (text.includes('error') || text.includes('failed') || text.includes('issue')) return 'error';
    if (text.includes('warning') || text.includes('caution') || text.includes('special')) return 'warning';
    if (text.includes('success') || text.includes('completed')) return 'success';
    return 'info';
  };

  const getMessageIcon = (severity, message) => {
    if (message?.messageType === 'brokerage') return <NotificationsActiveIcon fontSize="small" />;
    switch (severity) {
      case 'error': return <ErrorIcon fontSize="small" />;
      case 'warning': return <WarningIcon fontSize="small" />;
      case 'success': return <CheckCircleIcon fontSize="small" />;
      default: return <InfoIcon fontSize="small" />;
    }
  };

  if (processedMessages.length === 0 || currentIndex >= processedMessages.length) return null;

  const message = processedMessages[currentIndex];
  const severity = getMessageSeverity(message);

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ 
        top: { xs: 70, sm: 90 }, 
        maxWidth: { xs: 'calc(100% - 32px)', sm: '600px' }, 
        width: '100%', 
        right: { xs: 16, sm: 24 } 
      }}
    >
      <Alert
        onClose={handleClose}
        severity={severity}
        icon={getMessageIcon(severity, message)}
        sx={{
          width: '100%',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          alignItems: 'center',
          '& .MuiAlert-message': { 
            width: '100%', 
            pt: { xs: 0.5, sm: 1 }, 
            pb: { xs: 0.5, sm: 1 } 
          },
          '& .MuiAlert-icon': {
            mr: { xs: 1, sm: 2 }
          }
        }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.7,
                fontWeight: 600,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: { xs: '35px', sm: '40px' },
                textAlign: 'center'
              }}
            >
              {currentIndex + 1} / {processedMessages.length}
            </Typography>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              sx={{ ml: -0.5 }}
            >
              <CloseIcon fontSize="small" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
            </IconButton>
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.25, sm: 1 } }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 'bold',
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          >
            System Update
          </Typography>
          <Box sx={{ mb: 0.5 }}>
            {message.messageType === 'brokerage' ? (
              <Typography 
                variant="body2" 
                sx={{ 
                  wordBreak: 'break-word', 
                  lineHeight: 1.3,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                {message.messageText}
              </Typography>
            ) : (
              <>
                {message.genericMessage && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      wordBreak: 'break-word',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    {message.genericMessage}
                  </Typography>
                )}
                {message.bodMessage && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      wordBreak: 'break-word',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    {message.bodMessage}
                  </Typography>
                )}
                {message.holidayMessage && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      wordBreak: 'break-word',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    🎉 {message.holidayMessage}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default DashboardNotificationSnackbar;
